import React from "react";
import { Box, styled, SxProps, Theme } from "@mui/material";
import { SPACING_CONSTANTS } from "../../constants/styles/spacing";

interface DataCellProps {
  /**
   * 자식 컴포넌트들
   */
  children?: React.ReactNode;
  /**
   * 커스텀 스타일
   */
  sx?: SxProps<Theme>;
  /**
   * 컴포넌트 너비 (기본값: "280px")
   */
  width?: string | number;
  /**
   * 최소 높이 (기본값: "34px")
   */
  minHeight?: string | number;
}

interface DataCellAreaProps {
  /**
   * 자식 컴포넌트들
   */
  children?: React.ReactNode;
  /**
   * 커스텀 스타일
   */
  sx?: SxProps<Theme>;
  /**
   * 추가 CSS 클래스명
   */
  className?: string;
}

// Container 스타일을 동적으로 생성하는 함수
const createContainerStyles = (
  width?: string | number,
  minHeight?: string | number
) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: width || "100%",
  minHeight: minHeight || "34px",
  padding: `${SPACING_CONSTANTS.spacing[1]}px 0`, // 4px 0
});

const StyledLeftArea = styled(Box)({
  display: "flex",
  alignItems: "center",
  width: "120px",
  flexShrink: 0,
});

const StyledCenterArea = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  flex: "1",
  minWidth: "60px",
});

const StyledRightArea = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-end",
  width: "90px",
  flexShrink: 0,
});

/**
 * DataCell 컴포넌트
 *
 * 컴파운드 컴포넌트 패턴으로 구성된 유연한 레이아웃 컴포넌트입니다.
 * DataCell.Left, DataCell.Center, DataCell.Right를 사용하여 구성합니다.
 */
export const DataCell: React.FC<DataCellProps> & {
  Left: React.FC<DataCellAreaProps>;
  Center: React.FC<DataCellAreaProps>;
  Right: React.FC<DataCellAreaProps>;
} = ({ children, sx, width, minHeight }) => {
  const containerStyles = createContainerStyles(width, minHeight);

  return <Box sx={{ ...containerStyles, ...sx }}>{children}</Box>;
};

// Left 영역 컴포넌트
const DataCellLeft: React.FC<DataCellAreaProps> = ({ children, sx }) => (
  <StyledLeftArea sx={sx}>{children}</StyledLeftArea>
);

// Center 영역 컴포넌트
const DataCellCenter: React.FC<DataCellAreaProps> = ({ children, sx }) => (
  <StyledCenterArea sx={sx}>{children}</StyledCenterArea>
);

// Right 영역 컴포넌트
const DataCellRight: React.FC<DataCellAreaProps> = ({ children, sx }) => (
  <StyledRightArea sx={sx}>{children}</StyledRightArea>
);

DataCell.Left = DataCellLeft;
DataCell.Center = DataCellCenter;
DataCell.Right = DataCellRight;

export type { DataCellProps, DataCellAreaProps };
export { ValueText, RightContent } from "./RightContent";
export { CompareSection } from "./CompareSection";
export { Label } from "./Label";
export { LoadingContent } from "./LoadingContent";
export type { ValueTextProps, RightContentProps } from "./RightContent";
export type { CompareSectionProps } from "./CompareSection";
export type { LabelProps } from "./Label";
export type { LoadingContentProps } from "./LoadingContent";
